import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, MapPin, Pencil, X } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { StoreProfileDetail } from '../types';
import { buildInstoreLocationFloorplanRows } from '../lib/floorplanZoneUtils';
import { resolveStoreInstoreLocations, type RetailerLocationOption } from '../lib/inStoreLocationLabels';
import { ProvenanceBadge } from './ProvenanceBadge';

interface StoreInstoreLocationsCardProps {
  profile: StoreProfileDetail;
  storeId: number;
  storeIdParam: string;
  editable?: boolean;
}

function CardEditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      aria-label="Edit in-store locations"
      className="h-8 w-8 shrink-0 border border-slate-200 bg-slate-100 text-slate-800 shadow-sm hover:bg-slate-200 hover:text-slate-950 [&_svg]:text-slate-800"
      onClick={onClick}
    >
      <Pencil className="h-4 w-4" />
    </Button>
  );
}

export function StoreInstoreLocationsCard({
  profile,
  storeId,
  storeIdParam,
  editable = true,
}: StoreInstoreLocationsCardProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const retailerCatalog = profile.retailerInstoreLocations ?? [];
  const sortedCatalog = useMemo(
    () => [...retailerCatalog].sort((a, b) => a.name.localeCompare(b.name)),
    [retailerCatalog],
  );

  const [isEditing, setIsEditing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>(profile.storeInstoreLocationIds ?? []);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setSelectedIds(profile.storeInstoreLocationIds ?? []);
    }
  }, [profile.storeInstoreLocationIds, isEditing]);

  const storeLocations = useMemo(
    () => resolveStoreInstoreLocations(profile.storeInstoreLocationIds, sortedCatalog),
    [profile.storeInstoreLocationIds, sortedCatalog],
  );

  const mappingRows = useMemo(
    () => buildInstoreLocationFloorplanRows(storeLocations, profile.floorplanZones),
    [storeLocations, profile.floorplanZones],
  );

  const mappedCount = mappingRows.filter((row) => row.isMapped).length;

  const toggleLocation = (locationId: number, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) {
        return current.includes(locationId) ? current : [...current, locationId];
      }
      return current.filter((value) => value !== locationId);
    });
  };

  const invalidateQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
    ]);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiRequest(`/api/store-profiles/${storeId}/overview`, 'PATCH', {
        instoreLocationIds: [...selectedIds].sort((a, b) => a - b),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error ?? 'Failed to save in-store locations');
      }
      await invalidateQueries();
      setIsEditing(false);
      toast({ title: 'In-store locations updated' });
    } catch (error) {
      toast({
        title: 'Could not save locations',
        description: error instanceof Error ? error.message : 'Update failed',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0 gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4" aria-hidden />
            In-store locations
            <ProvenanceBadge provenance="live" />
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Which of this retailer&apos;s in-store locations exist here.
            Map each selected one to a shape on the floorplan.
          </p>
        </div>
        {editable && sortedCatalog.length > 0 && !isEditing && (
          <CardEditButton onClick={() => setIsEditing(true)} />
        )}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {sortedCatalog.length === 0 ? (
          <p className="text-gray-500">
            Couldn&apos;t load this retailer&apos;s in-store locations. Refresh the page, or check that the store is linked to the correct retailer.
          </p>
        ) : isEditing ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-600">
              These are the retailer&apos;s in-store locations. Tick which ones exist in this store — then map them on the floorplan.
            </p>
            <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-1 bg-white">
              {sortedCatalog.map((location: RetailerLocationOption) => {
                const checked = selectedIds.includes(location.id);
                return (
                  <label
                    key={location.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(value) => toggleLocation(location.id, value === true)}
                      aria-label={location.name}
                    />
                    <span className="flex-1 truncate">{location.name}</span>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={isSaving} onClick={() => void handleSave()}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : storeLocations.length === 0 ? (
          <div className="space-y-2">
            <p className="text-gray-500">
              No in-store locations selected for this store yet.
            </p>
            {editable && (
              <Button type="button" size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                Choose locations for this store
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span>{storeLocations.length} location{storeLocations.length === 1 ? '' : 's'} in this store</span>
              {profile.floorplanZones.length > 0 && (
                <Badge variant="secondary">
                  {mappedCount} of {storeLocations.length} on floorplan
                </Badge>
              )}
            </div>
            <ul className="space-y-1.5">
              {mappingRows.map(({ location, isMapped }) => (
                <li
                  key={location.id}
                  className="flex items-center gap-2 rounded-md border px-2.5 py-2 bg-white"
                >
                  <MapPin
                    className={`h-3.5 w-3.5 shrink-0 ${isMapped ? 'text-green-600' : 'text-amber-500'}`}
                    aria-hidden
                  />
                  <span className="flex-1 truncate font-medium">{location.name}</span>
                  <Badge variant={isMapped ? 'secondary' : 'outline'} className="text-[10px] shrink-0">
                    {isMapped ? 'On floorplan' : 'Not mapped'}
                  </Badge>
                </li>
              ))}
            </ul>
            {editable && (
              <Button type="button" size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                Edit selection
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
